import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import Swal from "sweetalert2";
import Select from "react-select";
import withReactContent from "sweetalert2-react-content";

interface TenantUser {
    tenant_user_id: number;
    user_id: number;
    user_first_name: string;
    user_last_name: string;
    email: string;
    avatar_url: string | null;
}

interface BotChannelAccount {
    external_chat_bot_channel_account_id: number;
    account_name: string;
    is_visible_to_tenant: boolean;
    external_chat_platform_id: number;
    external_chat_channel?: {
        external_chat_channel_id: number;
        external_chat_channel_display_name: string;
        external_chat_channel_key: string;
    };
    external_chat_platform?: {
        external_chat_platform_id: number;
        platform_display_name: string;
        platform_key: string;
    };
}

interface VisibilityUser {
    external_chat_bot_channel_account_message_visibility_user_id: number;
    external_chat_bot_channel_account: {
        external_chat_bot_channel_account_id: number;
        account_name: string;
        external_chat_platform_key: string;
        external_chat_channel_key: string;
    };
    user: {
        user_id: number;
        first_name: string;
        last_name: string;
        full_name: string;
        avatar_url: string | null;
    };
    tenant_id: number;
    external_chat_platform_id: number;
}

interface GroupedVisibility {
    channel_account_id: number;
    account_name: string;
    external_chat_channel_key: string;
    external_chat_platform_key?: string;
    users: VisibilityUser[];
}

const PrivacySettings = () => {
    const { selectedTenantId, auth } = useContext(AuthContext);
    const MySwal = withReactContent(Swal);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
    const [channels, setChannels] = useState<BotChannelAccount[]>([]);
    const [visibilityUsers, setVisibilityUsers] = useState<VisibilityUser[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<number | "" | "all">("");
    const [selectedChannelId, setSelectedChannelId] = useState<number | "">("");
    const [expandedChannels, setExpandedChannels] = useState<Set<number>>(new Set());
    const [editingVisibilityId, setEditingVisibilityId] = useState<number | null>(null);

    const [visibleToAllExpanded, setVisibleToAllExpanded] = useState(false);

    const fetchTenantUsers = async () => {
        if (!selectedTenantId) return;
        try {
            const response = await fetch(
                `${API_BASE_URL}/tenant-user/?tenant_id=${selectedTenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                setTenantUsers(data.results || []);
            }
        } catch (error) {
            console.error("Error fetching tenant users:", error);
        }
    };

    const fetchChannels = async () => {
        if (!selectedTenantId) return;
        try {
            const response = await fetch(
                `${API_BASE_URL}/external-chat-bot-channel-account/?tenant_id=${selectedTenantId}&is_active=true&chat_type_key=EXTERNAL`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                setChannels(data.results || data || []);
            }
        } catch (error) {
            console.error("Error fetching channels:", error);
        }
    };

    const fetchVisibilityUsers = async () => {
        if (!selectedTenantId) return;
        try {
            const response = await fetch(
                `${API_BASE_URL}/external-chat-bot-channel-account-message-visibility-user/?tenant_id=${selectedTenantId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                }
            );
            if (response.ok) {
                const data = await response.json();
                setVisibilityUsers(data.results || []);
            }
        } catch (error) {
            console.error("Error fetching visibility users:", error);
        }
    };



    const fetchAll = async () => {
        setLoading(true);
        await Promise.all([fetchTenantUsers(), fetchChannels(), fetchVisibilityUsers()]);
        setLoading(false);
    };

    useEffect(() => {
        fetchAll();
    }, [selectedTenantId]);


    const handleAddVisibilityUser = async () => {
        if (!selectedChannelId || !selectedTenantId) {
            MySwal.fire({
                title: "Error",
                text: "Please select an account.",
                icon: "error",
                confirmButtonText: "OK",
            });
            return;
        }

        if (selectedUserId === "all") {
            setSaving(true);
            try {
                const response = await fetch(
                    `${API_BASE_URL}/external-chat-bot-channel-account/${selectedChannelId}/`,
                    {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Basic ${localStorage.getItem("authCred")}`,
                        },
                        body: JSON.stringify({
                            tenant_id: selectedTenantId,
                            is_visible_to_tenant: true,
                            updated_by: auth?.user_id,
                        }),
                    }
                );

                if (response.ok) {
                    MySwal.fire({
                        title: "Success!",
                        text: "Account is now visible to all users!",
                        icon: "success",
                        confirmButtonText: "OK",
                    });
                    setSelectedUserId("");
                    setSelectedChannelId("");
                    await fetchChannels();
                } else {
                    MySwal.fire({
                        title: "Error!",
                        text: "Failed to update account visibility.",
                        icon: "error",
                        confirmButtonText: "OK",
                    });
                }
            } catch (error) {
                MySwal.fire({
                    title: "Error!",
                    text: "An error occurred while updating account visibility.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            } finally {
                setSaving(false);
            }
            return;
        }

        if (!selectedUserId) {
            MySwal.fire({
                title: "Error",
                text: "Please select a user.",
                icon: "error",
                confirmButtonText: "OK",
            });
            return;
        }

        setSaving(true);
        try {
            const url = editingVisibilityId
                ? `${API_BASE_URL}/external-chat-bot-channel-account-message-visibility-user/${editingVisibilityId}/`
                : `${API_BASE_URL}/external-chat-bot-channel-account-message-visibility-user/`;

            const method = editingVisibilityId ? "PATCH" : "POST";

            const response = await fetch(
                url,
                {
                    method: method,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Basic ${localStorage.getItem("authCred")}`,
                    },
                    body: JSON.stringify({
                        tenant_id: selectedTenantId,
                        external_chat_bot_channel_account_id: selectedChannelId,
                        user_id: selectedUserId,
                        external_chat_platform_id: channels.find(c => c.external_chat_bot_channel_account_id === Number(selectedChannelId))?.external_chat_platform_id,
                        updated_by: auth?.user_id,
                        ...(editingVisibilityId ? {} : { created_by: auth?.user_id })
                    }),
                }
            );

            if (response.ok) {
                MySwal.fire({
                    title: "Success!",
                    text: `User visibility ${editingVisibilityId ? "updated" : "added"} successfully!`,
                    icon: "success",
                    confirmButtonText: "OK",
                });
                setSelectedUserId("");
                setSelectedChannelId("");
                setEditingVisibilityId(null);
                await fetchVisibilityUsers();
            } else {
                MySwal.fire({
                    title: "Error!",
                    text: `Failed to ${editingVisibilityId ? "update" : "add"} user visibility.`,
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: `An error occurred while ${editingVisibilityId ? "updating" : "adding"} user visibility.`,
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleEditVisibilityUser = (vu: VisibilityUser) => {
        setSelectedUserId(vu.user.user_id);
        setSelectedChannelId(vu.external_chat_bot_channel_account.external_chat_bot_channel_account_id);
        setEditingVisibilityId(vu.external_chat_bot_channel_account_message_visibility_user_id);

        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setSelectedUserId("");
        setSelectedChannelId("");
        setEditingVisibilityId(null);
    };

    const handleDeleteVisibilityUser = async (id: number, userName: string) => {
        const result = await MySwal.fire({
            title: "Are you sure?",
            text: `Do you want to remove ${userName} from this channel?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, remove!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(
                    `${API_BASE_URL}/external-chat-bot-channel-account-message-visibility-user/${id}/`,
                    {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Basic ${localStorage.getItem("authCred")}`,
                        },
                    }
                );

                if (response.ok) {
                    MySwal.fire({
                        title: "Deleted!",
                        text: "User visibility removed successfully.",
                        icon: "success",
                        confirmButtonText: "OK",
                    });
                    await fetchVisibilityUsers();
                } else {
                    MySwal.fire({
                        title: "Error!",
                        text: "Failed to remove user visibility.",
                        icon: "error",
                        confirmButtonText: "OK",
                    });
                }
            } catch (error) {
                MySwal.fire({
                    title: "Error!",
                    text: "An error occurred while removing user visibility.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        }
    };

    const handleRemoveVisibleToAll = async (accountId: number, accountName: string) => {
        const result = await MySwal.fire({
            title: "Are you sure?",
            text: `Do you want to remove "${accountName}" from visible to all users?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, remove!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(
                    `${API_BASE_URL}/external-chat-bot-channel-account/${accountId}/`,
                    {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Basic ${localStorage.getItem("authCred")}`,
                        },
                        body: JSON.stringify({
                            tenant_id: selectedTenantId,
                            is_visible_to_tenant: false,
                            updated_by: auth?.user_id,
                        }),
                    }
                );

                if (response.ok) {
                    MySwal.fire({
                        title: "Removed!",
                        text: "Account is no longer visible to all users.",
                        icon: "success",
                        confirmButtonText: "OK",
                    });
                    await fetchChannels();
                } else {
                    MySwal.fire({
                        title: "Error!",
                        text: "Failed to update account visibility.",
                        icon: "error",
                        confirmButtonText: "OK",
                    });
                }
            } catch (error) {
                MySwal.fire({
                    title: "Error!",
                    text: "An error occurred while updating account visibility.",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        }
    };

    const getChannelIcon = (channelKey: string) => {
        if (!channelKey) return "fa-solid fa-message";
        switch (channelKey.toUpperCase()) {
            case "SMS":
                return "fa-solid fa-message";
            case "WHATSAPP":
                return "fa-brands fa-whatsapp";
            case "FACEBOOK":
                return "fa-brands fa-facebook";
            case "INSTAGRAM":
                return "fa-brands fa-instagram";
            case "LINE":
                return "fa-brands fa-line";
            case "SLACK":
                return "fa-brands fa-slack";
            case "TELEGRAM":
                return "fa-brands fa-telegram";
            default:
                return "fa-solid fa-message";
        }
    };

    const getChannelColor = (channelKey: string) => {
        if (!channelKey) return "#666666";
        switch (channelKey.toUpperCase()) {
            case "SMS":
                return "#4caf50";
            case "WHATSAPP":
                return "#25D366";
            case "FACEBOOK":
                return "#1877F2";
            case "INSTAGRAM":
                return "#E4405F";
            case "LINE":
                return "#00B900";
            case "SLACK":
                return "#4A154B";
            case "TELEGRAM":
                return "#2AABEE";
            default:
                return "#666666";
        }
    };

    const getChannelBgColor = (channelKey: string) => {
        if (!channelKey) return "rgba(102, 102, 102, 0.1)";
        switch (channelKey.toUpperCase()) {
            case "SMS":
                return "rgba(76, 175, 80, 0.1)";
            case "WHATSAPP":
                return "rgba(37, 211, 102, 0.1)";
            case "FACEBOOK":
                return "rgba(24, 119, 242, 0.1)";
            case "INSTAGRAM":
                return "rgba(228, 64, 95, 0.1)";
            case "LINE":
                return "rgba(0, 185, 0, 0.1)";
            case "SLACK":
                return "rgba(74, 21, 75, 0.1)";
            case "TELEGRAM":
                return "rgba(42, 171, 238, 0.1)";
            default:
                return "rgba(102, 102, 102, 0.1)";
        }
    };

    const renderPlatformIcon = (platformKey: string | undefined, channelKey: string | undefined, size: number = 18, containerStyle: any = { marginRight: '8px' }) => {
        const isEngati = !platformKey || platformKey === 'ENGATI';
        if (isEngati) {
            return (
                <i
                    className={getChannelIcon(channelKey || "")}
                    style={{
                        color: getChannelColor(channelKey || ""),
                        fontSize: `${size}px`,
                        ...containerStyle
                    }}
                ></i>
            );
        }
        const platformIconMap: Record<string, string> = {
            LAZADA: 'Lazada',
            TIKTOK: 'TikTok',
            SHOPEE: 'Shopee',
            GOOGLE_CHAT: 'Google-Chat',
        };
        const iconFileName = platformIconMap[platformKey.toUpperCase()] || (platformKey.charAt(0).toUpperCase() + platformKey.slice(1).toLowerCase());
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.9)', padding: '2px', borderRadius: '4px', ...containerStyle }}>
                <img
                    src={`/assets/img/platform-icons/${iconFileName}.png`}
                    alt={platformKey}
                    style={{ width: `${size}px`, height: `${size}px`, objectFit: 'contain' }}
                    onError={(e) => {
                        const target = e.currentTarget;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                            const fallback = document.createElement('i');
                            fallback.className = 'fa-solid fa-globe';
                            fallback.style.color = '#666666';
                            parent.appendChild(fallback);
                        }
                    }}
                />
            </span>
        );
    };

    const visibleToAllAccounts = channels.filter(c => c.is_visible_to_tenant);
    const availableChannels = channels.filter(c => !c.is_visible_to_tenant);

    const channelOptions = availableChannels.map(chan => ({
        value: chan.external_chat_bot_channel_account_id,
        label: chan.account_name,
        key: chan.external_chat_channel?.external_chat_channel_key,
        platformKey: chan.external_chat_platform?.platform_key
    }));

    const formatChannelOption = (option: any) => (
        <div className="d-flex align-items-center">
            {renderPlatformIcon(option.platformKey, option.key, 18, { marginRight: '10px' })}
            <span>{option.label}</span>
        </div>
    );

    const customSelectStyles = {
        control: (base: any) => ({
            ...base,
            height: '38px',
            minHeight: '38px',
            borderRadius: '6px',
            borderColor: '#e9ecef',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#0ea5e9'
            }
        }),
        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isFocused ? '#f0f9ff' : 'white',
            color: '#333',
            cursor: 'pointer',
            fontSize: '14px',
            '&:active': {
                backgroundColor: '#e0f2fe'
            }
        }),
        singleValue: (base: any) => ({
            ...base,
            fontSize: '14px'
        }),
        placeholder: (base: any) => ({
            ...base,
            fontSize: '14px'
        })
    };

    const toggleChannel = (channelId: number) => {
        setExpandedChannels((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(channelId)) {
                newSet.delete(channelId);
            } else {
                newSet.add(channelId);
            }
            return newSet;
        });
    };

    const groupedByChannel = (): GroupedVisibility[] => {
        const channelMap = new Map<number, GroupedVisibility>();

        availableChannels.forEach((channel) => {
            channelMap.set(channel.external_chat_bot_channel_account_id, {
                channel_account_id: channel.external_chat_bot_channel_account_id,
                account_name: channel.account_name,
                external_chat_channel_key: channel.external_chat_channel?.external_chat_channel_key || "",
                external_chat_platform_key: channel.external_chat_platform?.platform_key || "",
                users: [],
            });
        });

        visibilityUsers.forEach((vu) => {
            if (!vu.external_chat_bot_channel_account) return;
            const channelId = vu.external_chat_bot_channel_account.external_chat_bot_channel_account_id;
            const account = channels.find(c => c.external_chat_bot_channel_account_id === channelId);
            if (account?.is_visible_to_tenant) return;

            if (channelMap.has(channelId)) {
                const group = channelMap.get(channelId)!;
                group.users.push(vu);
                group.external_chat_channel_key = vu.external_chat_bot_channel_account.external_chat_channel_key;
                group.external_chat_platform_key = vu.external_chat_bot_channel_account.external_chat_platform_key;
            } else {
                channelMap.set(channelId, {
                    channel_account_id: vu.external_chat_bot_channel_account.external_chat_bot_channel_account_id,
                    account_name: vu.external_chat_bot_channel_account.account_name,
                    external_chat_channel_key: vu.external_chat_bot_channel_account.external_chat_channel_key,
                    external_chat_platform_key: vu.external_chat_bot_channel_account.external_chat_platform_key,
                    users: [vu],
                });
            }
        });

        return Array.from(channelMap.values()).filter((g) => g.users.length > 0);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    const grouped = groupedByChannel();

    return (
        <>


            <div className=" pt-3 mb-3">
                <h5 className="fw-semibold mb-1">Channel Account Visibility</h5>
                <p className="text-muted" style={{ fontSize: "13px" }}>Configure which users can see messages from specific channels</p>
            </div>

            <div className="row mb-4">
                <div className="col-md-5">
                    <label className="form-label">Select User</label>
                    <select
                        className="form-select"
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value === "all" ? "all" : e.target.value ? Number(e.target.value) : "")}
                    >
                        <option value="">Select a user...</option>
                        <option value="all">🌐 Show to All Users</option>
                        {tenantUsers.map((user) => (
                            <option key={user.user_id} value={user.user_id}>
                                {user.user_first_name} {user.user_last_name} ({user.email})
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-md-5">
                    <label className="form-label">Select Account</label>
                    <Select
                        classNamePrefix="react-select"
                        options={channelOptions}
                        value={channelOptions.find(opt => opt.value === selectedChannelId)}
                        onChange={(opt: any) => setSelectedChannelId(opt ? opt.value : "")}
                        formatOptionLabel={formatChannelOption}
                        styles={customSelectStyles}
                        placeholder="Select an account..."
                        isClearable
                    />
                </div>
                <div className="col-md-2 d-flex align-items-end gap-2">
                    <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleAddVisibilityUser}
                        disabled={saving}
                    >
                        {saving ? (editingVisibilityId ? "Updating..." : "Adding...") : (editingVisibilityId ? "Update" : "Add")}
                    </button>
                    {editingVisibilityId && (
                        <button
                            type="button"
                            className="btn btn-light w-100"
                            onClick={handleCancelEdit}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            <div className="channel-visibility-sections">
                {visibleToAllAccounts.length > 0 && (
                    <div
                        className="border rounded mb-3"
                        style={{
                            overflow: "hidden",
                            transition: "all 0.2s ease",
                        }}
                    >
                        <div
                            className="d-flex align-items-center justify-content-between px-3 py-3"
                            style={{
                                backgroundColor: "rgba(99, 102, 241, 0.1)",
                                cursor: "pointer",
                                transition: "background-color 0.2s ease",
                            }}
                            onClick={() => setVisibleToAllExpanded(!visibleToAllExpanded)}
                        >
                            <div className="d-flex align-items-center">
                                <i
                                    className="ti ti-world"
                                    style={{
                                        color: "#6366f1",
                                        fontSize: "22px",
                                        marginRight: "12px",
                                    }}
                                ></i>
                                <div>
                                    <h6 className="mb-0 fw-semibold">
                                        🌐 Visible to All Users
                                    </h6>
                                    <small className="text-muted">
                                        {visibleToAllAccounts.length} account{visibleToAllAccounts.length !== 1 ? "s" : ""} visible to all tenant users
                                    </small>
                                </div>
                            </div>
                            <div className="d-flex align-items-center">
                                <span
                                    className="badge me-3"
                                    style={{
                                        backgroundColor: "#6366f1",
                                        color: "#fff",
                                    }}
                                >
                                    {visibleToAllAccounts.length}
                                </span>
                                <i
                                    className={`ti ti-chevron-${visibleToAllExpanded ? "up" : "down"}`}
                                    style={{
                                        fontSize: "18px",
                                        color: "#6366f1",
                                        transition: "transform 0.2s ease",
                                    }}
                                ></i>
                            </div>
                        </div>

                        {visibleToAllExpanded && (
                            <div
                                className="px-3 py-3"
                                style={{
                                    backgroundColor: "#fafafa",
                                    borderTop: "1px solid rgba(99, 102, 241, 0.2)",
                                }}
                            >
                                <div className="d-flex flex-wrap gap-2">
                                    {visibleToAllAccounts.map((account) => (
                                        <div
                                            key={account.external_chat_bot_channel_account_id}
                                            className="d-inline-flex align-items-center px-3 py-2 rounded-pill"
                                            style={{
                                                backgroundColor: "#fff",
                                                border: "1px solid #e0e0e0",
                                                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                                transition: "all 0.2s ease",
                                            }}
                                        >
                                            {renderPlatformIcon(account.external_chat_platform?.platform_key, account.external_chat_channel?.external_chat_channel_key, 18, { marginRight: "8px" })}
                                            <span className="me-2" style={{ fontSize: "14px", fontWeight: 500 }}>
                                                {account.account_name}
                                            </span>
                                            <Link
                                                to="#"
                                                className="d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: "18px",
                                                    height: "18px",
                                                    borderRadius: "50%",
                                                    backgroundColor: "#ff4d4f",
                                                    color: "#fff",
                                                    fontSize: "10px",
                                                    textDecoration: "none",
                                                    transition: "background-color 0.2s ease",
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveVisibleToAll(
                                                        account.external_chat_bot_channel_account_id,
                                                        account.account_name
                                                    );
                                                }}
                                                title="Remove from visible to all"
                                            >
                                                <i className="ti ti-x"></i>
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {grouped.length === 0 && visibleToAllAccounts.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        <i className="ti ti-eye-off" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                        <p className="mt-3">No user visibility settings configured yet.</p>
                        <p className="small">Select a user and channel above to add visibility settings.</p>
                    </div>
                ) : (
                    grouped.map((group) => {
                        const isExpanded = expandedChannels.has(group.channel_account_id);
                        const channelKey = group.external_chat_channel_key;

                        return (
                            <div
                                key={group.channel_account_id}
                                className="border rounded mb-3"
                                style={{
                                    overflow: "hidden",
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <div
                                    className="d-flex align-items-center justify-content-between px-3 py-3"
                                    style={{
                                        backgroundColor: getChannelBgColor(channelKey),
                                        cursor: "pointer",
                                        transition: "background-color 0.2s ease",
                                    }}
                                    onClick={() => toggleChannel(group.channel_account_id)}
                                >
                                    <div className="d-flex align-items-center">
                                        {renderPlatformIcon(group.external_chat_platform_key, channelKey, 22, { marginRight: "12px" })}
                                        <div>
                                            <h6 className="mb-0 fw-semibold">
                                                {group.account_name}
                                            </h6>
                                            <small className="text-muted">
                                                {group.users.length} user{group.users.length !== 1 ? "s" : ""} with visibility
                                            </small>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <span
                                            className="badge me-3"
                                            style={{
                                                backgroundColor: getChannelColor(channelKey),
                                                color: "#fff",
                                            }}
                                        >
                                            {group.users.length}
                                        </span>
                                        <i
                                            className={`ti ti-chevron-${isExpanded ? "up" : "down"}`}
                                            style={{
                                                fontSize: "18px",
                                                color: getChannelColor(channelKey),
                                                transition: "transform 0.2s ease",
                                            }}
                                        ></i>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div
                                        className="px-3 py-3"
                                        style={{
                                            backgroundColor: "#fafafa",
                                            borderTop: `1px solid ${getChannelColor(channelKey)}20`,
                                        }}
                                    >
                                        <div className="d-flex flex-wrap gap-2">
                                            {group.users.map((vu) => (
                                                <div
                                                    key={vu.external_chat_bot_channel_account_message_visibility_user_id}
                                                    className="d-inline-flex align-items-center px-3 py-2 rounded-pill"
                                                    style={{
                                                        backgroundColor: "#fff",
                                                        border: "1px solid #e0e0e0",
                                                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                                        transition: "all 0.2s ease",
                                                    }}
                                                >
                                                    {vu.user?.avatar_url ? (
                                                        <img
                                                            src={vu.user.avatar_url}
                                                            alt=""
                                                            className="rounded-circle me-2"
                                                            style={{ width: "24px", height: "24px", objectFit: "cover" }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                                            style={{
                                                                width: "24px",
                                                                height: "24px",
                                                                backgroundColor: getChannelColor(channelKey),
                                                            }}
                                                        >
                                                            <span className="text-white" style={{ fontSize: "10px", fontWeight: 600 }}>
                                                                {vu.user?.full_name?.charAt(0)}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <span className="me-2" style={{ fontSize: "14px", fontWeight: 500 }}>
                                                        {vu.user?.full_name}
                                                    </span>
                                                    <Link
                                                        to="#"
                                                        className="d-flex align-items-center justify-content-center"
                                                        style={{
                                                            width: "18px",
                                                            height: "18px",
                                                            borderRadius: "50%",
                                                            backgroundColor: "#ff4d4f",
                                                            color: "#fff",
                                                            fontSize: "10px",
                                                            textDecoration: "none",
                                                            transition: "background-color 0.2s ease",
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteVisibilityUser(
                                                                vu.external_chat_bot_channel_account_message_visibility_user_id,
                                                                `${vu.user?.full_name}`
                                                            );
                                                        }}
                                                        title="Remove user"
                                                    >
                                                        <i className="ti ti-x"></i>
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </>
    );
};

export default PrivacySettings;
